import { useRef, useEffect, useCallback } from "react";
import {
  INFINITE_SCROLL_ROOT_MARGIN,
  INFINITE_SCROLL_THRESHOLD,
} from "../constants";

export interface UseInfiniteScrollObserverReturn {
  observerTarget: React.RefObject<HTMLDivElement>;
}

/**
 * Hook to handle intersection observer for infinite scrolling
 *
 * Sets up an IntersectionObserver that triggers fetchNextPage when
 * the observer target becomes visible and there are more pages to load.
 *
 * @param hasNextPage - Whether there are more pages to fetch
 * @param isFetchingNextPage - Whether currently fetching next page
 * @param isLoading - Whether initial data is loading
 * @param fetchNextPage - Function to fetch the next page
 * @returns Observer target ref to attach to a DOM element
 */
export function useInfiniteScrollObserver(
  hasNextPage: boolean,
  isFetchingNextPage: boolean,
  isLoading: boolean,
  fetchNextPage: () => void
): UseInfiniteScrollObserverReturn {
  const observerTarget = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (
        entry.isIntersecting &&
        hasNextPage &&
        !isFetchingNextPage &&
        !isLoading
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: INFINITE_SCROLL_ROOT_MARGIN,
      threshold: INFINITE_SCROLL_THRESHOLD,
    });

    const currentObserverTarget = observerTarget.current;

    if (currentObserverTarget) {
      observer.observe(currentObserverTarget);
      console.debug("Infinite scroll observer attached to target element");
    } else {
      console.debug("Infinite scroll observer target not found");
    }

    return () => {
      if (currentObserverTarget) {
        observer.unobserve(currentObserverTarget);
      }
    };
  }, [handleObserver]);

  return {
    observerTarget,
  };
}
